import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, ExternalLink, Loader as Loader2, Trophy, ArrowLeft, Copy } from 'lucide-react';
import { ContestService, OwnedContest } from '../services/ContestService';
import { SingAlongService } from '../services/SingAlongService';

export const ContestDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [contests, setContests] = useState<OwnedContest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [duplicateContest, setDuplicateContest] = useState<OwnedContest | null>(null);
  const [duplicating, setDuplicating] = useState(false);
  const [dupError, setDupError] = useState<string | null>(null);

  const loadContests = async () => {
    try {
      const userContests = await ContestService.getUserContests();
      setContests(userContests);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contests');
    }
  };

  useEffect(() => {
    (async () => {
      const user = await SingAlongService.getCurrentUser();
      if (!user || !user.email_confirmed_at) {
        navigate('/sing-along/signup', { replace: true });
        return;
      }

      try {
        const userContests = await ContestService.getUserContests();
        if (userContests.length === 0) {
          const hasSubscription = await ContestService.hasActiveSubscription();
          if (!hasSubscription) {
            navigate('/contests/plans', { replace: true });
            return;
          }
        }
        setContests(userContests);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load contests');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleDuplicate = async (name: string, slug: string) => {
    if (!duplicateContest) return;
    try {
      setDuplicating(true);
      setDupError(null);
      const available = await ContestService.checkSlugAvailable(slug);
      if (!available) {
        setDupError('That slug is already taken. Choose a different one.');
        return;
      }
      const newId = await ContestService.duplicateContest(duplicateContest.id, { name, slug });
      setDuplicateContest(null);
      await loadContests();
      navigate(`/contests/manage/${newId}`);
    } catch (err) {
      setDupError(err instanceof Error ? err.message : 'Failed to duplicate contest');
    } finally {
      setDuplicating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-12">
      <div className="absolute inset-0 bg-gradient-to-br from-sky-600/10 via-transparent to-emerald-600/10 pointer-events-none" />

      <div className="relative max-w-5xl mx-auto">
        <Link to="/contests" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6 transition">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black">My Contests</h1>
            <p className="text-white/50 text-sm mt-1">Manage your contests or create a new one</p>
          </div>
          <button
            onClick={() => navigate('/contests/setup')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-sky-500 to-emerald-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-sky-500/25 transition text-sm"
          >
            <Plus size={18} />
            New Contest
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
            {error}
          </div>
        )}

        {contests.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-sky-500/10 flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-sky-400" />
            </div>
            <h2 className="text-xl font-bold mb-2">No contests yet</h2>
            <p className="text-white/50 mb-6">Create your first contest to get started</p>
            <button
              onClick={() => navigate('/contests/setup')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-sky-500 to-emerald-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-sky-500/25 transition"
            >
              <Plus size={18} />
              Create Contest
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {contests.map((contest) => (
              <div
                key={contest.id}
                className="group relative bg-white/[0.03] border border-white/10 rounded-2xl p-5 hover:border-white/20 hover:bg-white/[0.05] transition-all cursor-pointer"
                onClick={() => navigate(`/contests/manage/${contest.id}`)}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      backgroundColor: `${contest.primary_color || '#0ea5e9'}20`,
                      color: contest.primary_color || '#0ea5e9',
                    }}
                  >
                    <Trophy size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-white truncate">{contest.name}</h3>
                    <p className="text-white/40 text-xs font-mono truncate">/league/{contest.slug}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      contest.status === 'open'
                        ? 'bg-green-500/10 text-green-400'
                        : 'bg-slate-500/10 text-slate-400'
                    }`}
                  >
                    {contest.status === 'open' ? 'Active' : contest.status}
                  </span>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setDuplicateContest(contest)}
                      className="p-1.5 text-white/30 hover:text-emerald-400 transition rounded-lg hover:bg-emerald-500/10"
                      title="Duplicate contest"
                    >
                      <Copy size={15} />
                    </button>
                    <a
                      href={`/league/${contest.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1.5 text-white/30 hover:text-sky-400 transition"
                    >
                      <ExternalLink size={15} />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {duplicateContest && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <DuplicateContestModal
            contest={duplicateContest}
            loading={duplicating}
            error={dupError}
            onClose={() => { setDuplicateContest(null); setDupError(null); }}
            onConfirm={handleDuplicate}
          />
        </div>
      )}
    </div>
  );
};

interface DuplicateContestModalProps {
  contest: OwnedContest;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (name: string, slug: string) => void;
}

const DuplicateContestModal: React.FC<DuplicateContestModalProps> = ({ contest, loading, error, onClose, onConfirm }) => {
  const [name, setName] = useState(`Copy of ${contest.name}`);
  const [slug, setSlug] = useState(`copy-of-${contest.slug}`);

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
      <h3 className="text-lg font-bold text-white mb-2">Duplicate Contest</h3>
      <p className="text-sm text-white/60 mb-4">
        This will create a new contest with the same settings, branding, and songs. Participants and scores will not be copied.
      </p>
      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
          {error}
        </div>
      )}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-white/70 mb-1">Contest Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-sky-400 focus:outline-none"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-white/70 mb-1">Slug</label>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-sky-400 focus:outline-none font-mono text-sm"
          />
          <p className="text-xs text-white/40 mt-1">Your contest will live at /league/{slug}</p>
        </div>
      </div>
      <div className="flex gap-3 mt-6">
        <button
          onClick={onClose}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-slate-600 text-white rounded-lg hover:bg-slate-700 transition disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => onConfirm(name, slug)}
          disabled={loading || !name.trim() || !slug.trim()}
          className="flex-1 px-4 py-2 bg-gradient-to-r from-sky-500 to-emerald-500 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-sky-500/25 transition disabled:opacity-50"
        >
          {loading ? 'Duplicating...' : 'Duplicate'}
        </button>
      </div>
    </div>
  );
};

export default ContestDashboard;
